import pluralize from "pluralize"
import { useContext, useEffect, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"

import {
  queryGeneralLedgerCodeSyncStatus,
  syncGeneralLedgerCodes,
} from "src/api/Accounting"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { AccountingContext } from "./AccountingView"

const PROCESSING = "Processing"
const SUCCESSFULLY_IMPORTED = "Successfully imported"

const useAccountsAndItemsImport = ({ source } = {}) => {
  const { accountingV2Enabled, marinaSlug, selectedAccountingService } =
    useContext(AccountingContext)
  const tracker = useTracker()
  const showToast = useToast()
  const queryClient = useQueryClient()
  const [showManualCodesConfirmation, setShowManualCodesConfirmation] =
    useState(false)

  const { data: syncStatus } = useQuery({
    queryKey: ["syncGeneralLedgerCodes:status", marinaSlug],
    queryFn: () => queryGeneralLedgerCodeSyncStatus(marinaSlug),
    refetchInterval: (syncStatus) =>
      [PROCESSING, "Loading"].includes(syncStatus.status) && 2000,
    refetchOnWindowFocus: false,
    retry: false,
    initialData: { status: "Loading" },
  })

  // Whichever tab started it, an import changes the items and their sync errors, and Item Mapping keeps its
  // products query fresh forever, so clear both here.
  const previousStatus = useRef(syncStatus.status)
  useEffect(() => {
    if (
      previousStatus.current === PROCESSING &&
      syncStatus.status === SUCCESSFULLY_IMPORTED
    ) {
      queryClient.invalidateQueries("invoice_items")
      queryClient.invalidateQueries("products")
    }
    previousStatus.current = syncStatus.status
  }, [syncStatus.status, queryClient])

  const { mutate: synchronize, isLoading: isSynchronizing } = useMutation({
    mutationFn: () => {
      queryClient.setQueryData(
        ["syncGeneralLedgerCodes:status", marinaSlug],
        () => ({ status: PROCESSING })
      )

      return syncGeneralLedgerCodes(marinaSlug)
    },
    onMutate: () => {
      showToast(
        "Import in progress. Please allow a few minutes for completion.",
        {
          type: "success",
          duration: 12,
        }
      )
    },
    onError: (error) => {
      showToast(error.toString(), { type: "error", duration: 12 })
    },
  })

  const hasImportedCodes = [SUCCESSFULLY_IMPORTED, "Failed"].includes(
    syncStatus.status
  )
  const isImportingCodes = syncStatus.status === PROCESSING || isSynchronizing
  const manualCodesCount = syncStatus.manualCodesCount || 0

  const fireImport = () => {
    const baseEvent = accountingV2Enabled
      ? "accounting:import_accounts_and_items"
      : "accounting:import_accounts"

    tracker.trackEvent(
      hasImportedCodes ? `${baseEvent}_resync_pressed` : `${baseEvent}_pressed`,
      {
        accounting_system: selectedAccountingService.serviceId,
        source,
      }
    )

    synchronize()
  }

  const onImportClick = () => {
    if (manualCodesCount > 0) {
      setShowManualCodesConfirmation(true)
      return
    }
    fireImport()
  }

  const manualCodesConfirmation = {
    isOpen: showManualCodesConfirmation,
    onClose: () => setShowManualCodesConfirmation(false),
    onConfirm: () => {
      setShowManualCodesConfirmation(false)
      fireImport()
    },
    title: "Replace manually-managed codes?",
    message: `This marina has ${pluralize(
      "manually-managed G/L code",
      manualCodesCount,
      true
    )}. Syncing will replace any manually-managed codes with codes from your accounting system.`,
    confirmLabel: "Replace and sync",
  }

  return {
    hasImportedCodes,
    isImportingCodes,
    manualCodesConfirmation,
    onImportClick,
    syncStatus,
  }
}

export default useAccountsAndItemsImport
